import { Component, OnInit, Input, forwardRef, OnChanges, SimpleChanges, EventEmitter, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, of, Subject, concat } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap, switchMap, catchError } from 'rxjs/operators';

import { AutocompleteInterface } from '../../interfaces/autocomplete.interface';

@Component({
  selector: 'app-form-autocomplete',
  templateUrl: './form-autocomplete.component.html',
  styleUrls: ['./form-autocomplete.component.sass'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormAutocompleteComponent),
      multi: true
    }
  ]
})
export class FormAutocompleteComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() name: string;
  @Input() placeholder: string;
  @Input() items = [];
  @Input() limit = 20;
  @Input() required = false;
  @Input() firstChars = 0;
  @Input() disabled = false;
  @Input() resetable = false;
  @Input() clearable = false;
  @Input() template = 'default';
  @Input() service: AutocompleteInterface;
  @Input() optionalId: string; // id used to call a service with an id needed
  @Output() change: EventEmitter<any>;

  items$: Observable<any>;
  input$ = new Subject<string>();
  loading = false;
  selectedItem: string;
  id:string;

  constructor() {
    this.change = new EventEmitter();
  }

  propagateChange = (_: any) => {};
  propagateTouch = (_: any) => {};

  ngOnInit() {
    if (this.optionalId) {
      this.id=this.optionalId
    }
    this.loadItems();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.items) {
      const value = changes.items.currentValue;

      if (value && !Array.isArray(value)) {
        this.items$ = of([ this.items ]);
      } else {
        this.items$ = of(this.items);
      }

      this.loadItems();
    }
  }

  onChange(event) {
    this.change.emit(event);
  }

  onModelChange(item: string) {
    this.propagateChange(item);
    this.propagateTouch(item);
  }

  writeValue(value: any) {
    if (value && typeof value === 'object') {
      value = value.id;
    }

    this.selectedItem = value;
  }

  registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any) {
    this.propagateTouch = fn;
  }

  loadItems() {
    this.items$ = concat(
      of(this.items),
      this.input$.pipe(
        debounceTime(200),
        distinctUntilChanged(),
        tap(() => {
          this.loading = true;
        }),
        switchMap((term) => {
          if (this.optionalId){
            return this.service.autocomplete(term, this.limit, this.optionalId).pipe(
              catchError(() => {
                return of([]);
              }),
              tap(() => {
                this.loading = false;
              })
            );
          }
          return this.service.autocomplete(term, this.limit).pipe(
            catchError(() => {
              return of([]);
            }),
            tap(() => {
              this.loading = false;
            })
          );
        })
      )
    );
  }

  trackByFn(item: any) {
    return item.id;
  }
}
